import {
  History as HistoryIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  MeetingRoom as FittingRoomIcon,
  MyLocation as MyLocationIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AdvancedMarker,
  APIProvider,
  Map,
  Marker,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { AsYouType, type CountryCode } from "libphonenumber-js/max";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AdminDialog, PageShell } from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { StoreLabelPreview } from "@/components/common/StoreLabelPreview";
import {
  currencyCodes,
  currencyDisplayNames,
  currencySymbols,
  localeDisplayNames,
  locales,
} from "@/constants/store";
import { type Customer, CustomerService } from "@/services/features/CustomerService";
import { Region, type Store, StoreService } from "@/services/features/StoreService";
import { formatPostalAddressForCountry } from "@/utils/postalAddress";
import { formatStoreSelectorLabel } from "@/utils/store";
import { getBrowserTimezone, getStoreDayStartUtcTimestamp } from "@/utils/storeTime";

interface StoreFormData {
  name: string;
  phoneNumber: string;
  address: string;
  street: string;
  city: string;
  country: string;
  state: string;
  zip: string;
  externalId: string;
  group: string;
  openDate: Date | null;
  currencyCode?: string;
  currencySymbol?: string;
  locale?: string;
  timezone: string;
  autoCleanEnabled: boolean;
  regionId?: number;
}

interface EditStoreLocationState {
  returnTo?: string;
}

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
const GOOGLE_MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID;
const DEFAULT_MAP_CENTER = { lat: 39.833333, lng: -98.583333 };
// US and Canada use the same North American Numbering Plan format. A leading '+' overrides this.
const DEFAULT_PHONE_COUNTRY: CountryCode = "US";

const formatPhoneNumber = (phoneNumber: string): string =>
  phoneNumber ? new AsYouType(DEFAULT_PHONE_COUNTRY).input(phoneNumber) : "";

const serializePhoneNumber = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, "");

  return phoneNumber.trim().startsWith("+") ? `+${digits}` : digits;
};

interface GeoPoint {
  lat: number;
  lng: number;
}

interface GoogleGeocoderLoaderProps {
  onReady: (geocoder: google.maps.Geocoder | null) => void;
}

const GoogleGeocoderLoader = ({ onReady }: GoogleGeocoderLoaderProps) => {
  const geocodingLibrary = useMapsLibrary("geocoding");
  const geocoder = React.useMemo(
    () => (geocodingLibrary ? new geocodingLibrary.Geocoder() : null),
    [geocodingLibrary],
  );

  useEffect(() => {
    onReady(geocoder);
    return () => onReady(null);
  }, [geocoder, onReady]);

  return null;
};

interface StoreLocationMapProps {
  position: GeoPoint | null;
  markerTitle: string;
  defaultZoom?: number;
  onGeocoderReady?: (geocoder: google.maps.Geocoder | null) => void;
}

const StoreLocationMap = ({
  position,
  markerTitle,
  defaultZoom,
  onGeocoderReady,
}: StoreLocationMapProps) => {
  const center = position || DEFAULT_MAP_CENTER;
  const mapViewKey = position ? `${position.lat},${position.lng}` : "default";

  if (!GOOGLE_MAPS_KEY) {
    return null;
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_KEY} version="weekly">
      {onGeocoderReady && <GoogleGeocoderLoader onReady={onGeocoderReady} />}
      <Map
        key={mapViewKey}
        defaultCenter={center}
        defaultZoom={defaultZoom ?? (position ? 15 : 4)}
        mapId={GOOGLE_MAP_ID}
        disableDefaultUI
        zoomControl
        style={{ width: "100%", height: "100%" }}
      >
        {position &&
          (GOOGLE_MAP_ID ? (
            <AdvancedMarker position={position} title={markerTitle} />
          ) : (
            <Marker position={position} title={markerTitle} />
          ))}
      </Map>
    </APIProvider>
  );
};

const sectionHeadingSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  color: "primary.main",
  fontSize: { xs: "1.1rem", sm: "1.15rem" },
} as const;
const locationDetailLabelSx = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

const toFiniteNumber = (value: unknown) => {
  if (typeof value === "string" && value.trim() === "") return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const resolveGeoPoint = (value: unknown) => {
  if (!value || typeof value !== "object") return null;

  const point = value as {
    lat?: unknown;
    lon?: unknown;
  };
  const latitude = toFiniteNumber(point.lat);
  const longitude = toFiniteNumber(point.lon);

  return latitude === null || longitude === null ? null : { lat: latitude, lng: longitude };
};

const getStoreAddress = (store: Store) =>
  store.address?.trim() ||
  [store.street, store.city, store.state, store.zip, store.country]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(", ");

const StoreForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, customerName, storeId } = useParams();
  const { t } = useTranslation();
  const isEditMode = Boolean(storeId);
  const browserTimezone = getBrowserTimezone();
  const [existingStore, setExistingStore] = useState<Store | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const returnTo = (location.state as EditStoreLocationState | null)?.returnTo;
  const storesPath =
    typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? returnTo
      : "/admin/organization/stores";
  const titleForCustomer = React.useMemo(
    () => t("management.stores.addStore.titleForCustomer", { customerName }),
    [customerName, t],
  );
  const documentTitle = React.useMemo(
    () => t("management.stores.addStore.documentTitle", { customerName }),
    [customerName, t],
  );
  const pageTitle = isEditMode
    ? existingStore
      ? t("management.stores.editStore.titleWithStoreName", { storeName: existingStore.name })
      : t("management.stores.editStore.title")
    : titleForCustomer;
  const pageDocumentTitle = isEditMode ? pageTitle : documentTitle;
  const { enqueueSnackbar } = useSnackbar();
  const storeService = StoreService.getInstance();
  const [loading, setLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(isEditMode);
  const [locationFound, setLocationFound] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [locationPosition, setLocationPosition] = useState<GeoPoint | null>(null);
  const [isLegacyFieldsDialogOpen, setIsLegacyFieldsDialogOpen] = useState(false);
  const [isOriginalAddressDialogOpen, setIsOriginalAddressDialogOpen] = useState(false);
  const [originalAddress, setOriginalAddress] = useState("");
  const [hasGeolocatedInSession, setHasGeolocatedInSession] = useState(false);

  const [formData, setFormData] = useState<StoreFormData>({
    name: "",
    phoneNumber: "",
    address: "",
    street: "",
    city: "",
    country: "",
    state: "",
    zip: "",
    externalId: "",
    group: "",
    openDate: null,
    currencyCode: undefined,
    currencySymbol: "",
    locale: undefined,
    timezone: "",
    autoCleanEnabled: false,
    regionId: undefined,
  });
  const canViewOriginalAddress =
    Boolean(formData.address.trim()) &&
    (hasGeolocatedInSession || (isEditMode && formData.address.trim() !== originalAddress.trim()));
  const canRetainGeocodedLocation = hasGeolocatedInSession || (isEditMode && locationFound);

  useEffect(() => {
    document.title = pageDocumentTitle;
  }, [pageDocumentTitle]);

  useEffect(() => {
    if (isEditMode || !customerId) return;

    let cancelled = false;
    void CustomerService.getInstance()
      .getCustomerById(Number(customerId))
      .then((response) => {
        if (!cancelled) setCustomer(response.success ? (response.data ?? null) : null);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      void loadStoreData();
      return;
    }

    if (customerId) {
      void loadRegions(Number(customerId));
    }
    setIsInitialLoading(false);
  }, [customerId, isEditMode, storeId]);

  const loadRegions = async (customerIdValue: number) => {
    try {
      const response = await storeService.getRegionsByCustomerId(customerIdValue);
      if (response.success && response.data) {
        setRegions(response.data);
      }
    } catch {
      enqueueSnackbar(t("management.stores.addStore.notifications.failedLoadRegions"), {
        variant: "error",
      });
    }
  };

  const loadStoreData = async () => {
    if (!storeId) return;

    setIsInitialLoading(true);
    try {
      const response = await storeService.getStoreById(Number(storeId));
      if (!response.data) return;

      const store = response.data;
      const hasStoredAddress = Boolean(store.address?.trim());
      const existingLocation = resolveGeoPoint(store.geoPoint);
      const storeAddress = getStoreAddress(store);
      setExistingStore(store);
      setFormData({
        name: store.name,
        phoneNumber: store.phoneNumber || "",
        address: storeAddress,
        street: store.street || "",
        city: store.city || "",
        country: store.country || "",
        state: store.state || "",
        zip: store.zip || "",
        externalId: store.externalId || "",
        group: store.group || "",
        openDate: store.openDate
          ? moment.tz(store.openDate, store.timezone || browserTimezone).toDate()
          : null,
        currencyCode: store.currencyCode || undefined,
        currencySymbol: store.currencySymbol || "",
        locale: store.locale || undefined,
        timezone: store.timezone || "",
        autoCleanEnabled: store.autoCleanEnabled || false,
        regionId: store.regionId,
      });
      setOriginalAddress(storeAddress);
      setHasGeolocatedInSession(false);
      setLocationPosition(existingLocation);
      // Legacy stores without a canonical address must be geocoded before their
      // reconstructed address can be saved back to the API.
      setLocationFound(hasStoredAddress && Boolean(store.timezone) && Boolean(existingLocation));
      await loadRegions(store.customerId);
    } catch {
      enqueueSnackbar(t("management.stores.addStore.notifications.failedCreateStore"), {
        variant: "error",
      });
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleInputChange = (field: keyof StoreFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneNumberChange = (phoneNumber: string) => {
    handleInputChange("phoneNumber", formatPhoneNumber(phoneNumber));
  };

  const geocodeAddress = async () => {
    if (!geocoder) {
      enqueueSnackbar(t("management.stores.addStore.notifications.mapNotInitialized"), {
        variant: "error",
      });
      return;
    }

    if (!formData.address) {
      enqueueSnackbar(t("management.stores.addStore.notifications.addressRequired"), {
        variant: "warning",
      });
      return;
    }

    if (!GOOGLE_MAPS_KEY) {
      enqueueSnackbar(t("management.stores.addStore.notifications.googleMapsApiKeyNotFound"), {
        variant: "error",
      });
      return;
    }

    setOriginalAddress(formData.address);
    setLoading(true);
    try {
      const { results } = await geocoder.geocode({ address: formData.address });

      if (!results.length) {
        enqueueSnackbar(t("management.stores.addStore.notifications.noResultsFoundForAddress"), {
          variant: "warning",
        });
        setLocationFound(false);
        return;
      }

      const result = results[0];
      const location = result.geometry.location;
      const position = { lat: location.lat(), lng: location.lng() };
      setLocationPosition(position);

      const findAddressComponent = (type: string) =>
        result.address_components.find((component: { types: string[] }) =>
          component.types.includes(type),
        );
      const country = findAddressComponent("country");
      const state = findAddressComponent("administrative_area_level_1");
      const countryCode = country?.short_name || "";
      const postalCodeComponent = findAddressComponent("postal_code")?.long_name;
      const postalCode = postalCodeComponent || "N/A";
      const street = [
        findAddressComponent("street_number")?.long_name,
        findAddressComponent("route")?.long_name,
      ]
        .filter(Boolean)
        .join(" ");
      const city =
        findAddressComponent("locality")?.long_name ||
        findAddressComponent("postal_town")?.long_name ||
        findAddressComponent("sublocality_level_1")?.long_name ||
        findAddressComponent("administrative_area_level_2")?.long_name ||
        state?.long_name ||
        "";
      const subpremise = findAddressComponent("subpremise")?.long_name?.trim() || "";
      const formattedSubpremise = /^\d+$/.test(subpremise) ? `#${subpremise}` : subpremise;
      const streetAddress = [street, formattedSubpremise].filter(Boolean).join(" ");
      let normalizedAddress = result.formatted_address;

      if (countryCode) {
        normalizedAddress =
          (await formatPostalAddressForCountry({
            addressLines: streetAddress,
            administrativeArea: state?.short_name || "",
            countryCode,
            countryName: country?.long_name || "",
            locality: city,
            postalCode: postalCodeComponent || "",
          })) || result.formatted_address;
      }

      setFormData((previous) => ({
        ...previous,
        address: normalizedAddress,
        street: streetAddress,
        city,
        country: country?.long_name || "",
        state: state?.short_name || country?.long_name || "",
        zip: postalCode,
      }));
      if (!result.place_id) {
        throw new Error(
          t("management.stores.addStore.notifications.geocodingFailed", {
            errorMessage: "Geocoding result did not include a Place ID",
          }),
        );
      }

      const placesLibrary = await google.maps.importLibrary("places");
      const place = new placesLibrary.Place({ id: result.place_id });

      await place.fetchFields({ fields: ["timeZone"] });

      const timeZoneId = place.timeZone?.id;

      if (!timeZoneId) {
        throw new Error(
          t("management.stores.addStore.notifications.timezoneLookupFailed", {
            errorMessage: "Place did not include a timezone",
          }),
        );
      }

      setFormData((previous) => ({
        ...previous,
        timezone: timeZoneId,
      }));
      enqueueSnackbar(t("management.stores.addStore.notifications.locationFound"), {
        variant: "success",
      });

      setLocationFound(true);
      setHasGeolocatedInSession(true);
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : t("management.stores.addStore.notifications.errorGeocodingStoreAddress"),
        { variant: "error" },
      );
      setLocationFound(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedLocation = locationPosition;
    if (isEditMode && !storeId) return;
    if (!selectedLocation || !locationFound || (!isEditMode && !customerId)) return;
    const storeLocation = selectedLocation!;

    setSubmissionError(null);
    setLoading(true);
    try {
      const storeTimezone = formData.timezone || browserTimezone;
      const phoneNumber = serializePhoneNumber(formData.phoneNumber);

      if (isEditMode) {
        const response = await storeService.editStore({
          id: Number(storeId),
          name: formData.name,
          address: formData.address,
          phoneNumber,
          externalId: formData.externalId,
          group: formData.group,
          openDate: formData.openDate
            ? getStoreDayStartUtcTimestamp(formData.openDate, storeTimezone, browserTimezone)
            : undefined,
          regionId: formData.regionId,
          autoCleanEnabled: formData.autoCleanEnabled,
          currencyCode: formData.currencyCode,
          currencySymbol: formData.currencySymbol || "",
          locale: formData.locale || null,
          street: formData.street,
          city: formData.city,
          country: formData.country,
          state: formData.state,
          zip: formData.zip,
          timezone: formData.timezone,
          geoPoint: {
            lat: storeLocation.lat,
            lon: storeLocation.lng,
          },
        });

        if (response.success) {
          enqueueSnackbar(
            t("management.stores.editStore.messages.storeUpdatedBody", {
              storeName: formData.name,
            }),
            { variant: "success" },
          );
          await navigate(storesPath);
        } else {
          setSubmissionError(
            t("management.stores.editStore.errors.failedUpdate", {
              errorMessage:
                response.error || t("management.stores.addStore.notifications.unknownError"),
            }),
          );
        }
        return;
      }

      const storeData = {
        name: formData.name,
        customerId: Number(customerId),
        customerName: customerName || "",
        address: formData.address,
        street: formData.street,
        city: formData.city,
        country: formData.country,
        state: formData.state,
        zip: formData.zip,
        phoneNumber,
        group: formData.group,
        openDate: formData.openDate
          ? getStoreDayStartUtcTimestamp(formData.openDate, storeTimezone, browserTimezone)
          : getStoreDayStartUtcTimestamp(new Date(), storeTimezone, browserTimezone),
        timezone: formData.timezone,
        geoPoint: {
          lat: storeLocation.lat,
          lon: storeLocation.lng,
        },
        externalId: formData.externalId,
        currencyCode: formData.currencyCode,
        currencySymbol: formData.currencySymbol || "",
        locale: formData.locale,
        autoCleanEnabled: formData.autoCleanEnabled,
        regionId: formData.regionId,
        live: true,
        active: true,
        global: false,
        inProgressUploadCount: 0,
        successfulUploadCount: 0,
        uploadDelta: 0,
        shouldRestartShopper: false,
      };

      const response = await storeService.addStore(storeData);
      if (response.success && response.data) {
        enqueueSnackbar(
          t("management.stores.addStore.messages.storeAdded", {
            storeName: formData.name,
          }),
          { variant: "success" },
        );
        navigate("/admin/organization/stores/edit-rooms/" + response.data.id + "?create=true", {
          state: { returnTo: storesPath },
        });
      } else {
        setSubmissionError(
          t("management.stores.addStore.errors.failedCreate", {
            errorMessage:
              response.error || t("management.stores.addStore.notifications.unknownError"),
          }),
        );
      }
    } catch (error) {
      if (isEditMode) {
        setSubmissionError(
          t("management.stores.editStore.errors.failedUpdate", {
            errorMessage:
              error instanceof Error
                ? error.message
                : t("management.stores.addStore.notifications.unknownError"),
          }),
        );
        return;
      }

      setSubmissionError(
        t("management.stores.addStore.errors.failedCreate", {
          errorMessage:
            error instanceof Error
              ? error.message
              : t("management.stores.addStore.notifications.unknownError"),
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  if (isEditMode && isInitialLoading) {
    return (
      <PageShell breadcrumbs="admin" title={pageTitle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs="admin" title={pageTitle}>
      {submissionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submissionError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack
          spacing={3}
          sx={{
            "& .MuiTextField-root .MuiInputLabel-root": {
              fontSize: "1rem",
            },
            "& .MuiTextField-root .MuiInputBase-input, & .MuiTextField-root textarea": {
              fontSize: "max(1rem, 16px)",
            },
          }}
        >
          {/* Basic Information */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={sectionHeadingSx}>
                <StoreIcon />
                {t("management.stores.addStore.sections.basicInformation")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "repeat(6, minmax(0, 1fr))" },
                  gap: 3,
                }}
              >
                <Box sx={{ gridColumn: { lg: "span 3" } }}>
                  <TextField
                    fullWidth
                    required
                    size="medium"
                    label={t("management.stores.addStore.fields.storeName")}
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder={t("management.stores.addStore.placeholders.storeName")}
                  />
                </Box>
                <Box sx={{ gridColumn: { lg: "span 3" } }}>
                  <TextField
                    fullWidth
                    size="medium"
                    label={t("management.stores.addStore.fields.phoneNumber")}
                    value={formData.phoneNumber}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    placeholder={t("management.stores.addStore.placeholders.phoneNumber")}
                    type="tel"
                    autoComplete="tel"
                  />
                </Box>
                <Box sx={{ gridColumn: { lg: "span 2" } }}>
                  <DatePicker
                    label={t("management.stores.addStore.fields.openDate")}
                    value={formData.openDate}
                    onChange={(date) => handleInputChange("openDate", date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        placeholder: t("management.stores.addStore.placeholders.openDate"),
                        helperText: t("management.stores.addStore.helperText.openDate"),
                      },
                    }}
                  />
                </Box>
                <Box sx={{ gridColumn: { lg: "span 2" } }}>
                  <TextField
                    fullWidth
                    required
                    size="medium"
                    label={t("management.stores.addStore.fields.externalId")}
                    value={formData.externalId}
                    onChange={(e) => handleInputChange("externalId", e.target.value)}
                    placeholder={t("management.stores.addStore.placeholders.externalId")}
                  />
                </Box>
                <Box sx={{ gridColumn: { lg: "span 2" } }}>
                  <TextField
                    fullWidth
                    size="medium"
                    label={t("management.stores.addStore.fields.group")}
                    value={formData.group}
                    onChange={(e) => handleInputChange("group", e.target.value)}
                    placeholder={t("management.stores.addStore.placeholders.group")}
                  />
                </Box>
                <Box sx={{ gridColumn: { lg: "span 6" } }}>
                  <StoreLabelPreview
                    store={{
                      id: storeId ?? "123",
                      customerId:
                        existingStore?.customerId ?? (customerId ? Number(customerId) : undefined),
                      customerInitial: isEditMode
                        ? existingStore?.customerInitial
                        : customer?.id === Number(customerId)
                          ? customer.initial
                          : undefined,
                      name: formData.name,
                      externalId: formData.externalId,
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={sectionHeadingSx}>
                <LocationIcon />
                {t("management.stores.addStore.sections.location")}
              </Typography>

              {!locationFound ? (
                <Alert severity="warning">
                  {t("management.stores.editStore.locationRequired.warning")}
                </Alert>
              ) : null}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(360px, 1fr)" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    height: { xs: 300, lg: 400 },
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <StoreLocationMap
                    position={locationPosition}
                    markerTitle={
                      formData.address || t("management.stores.addStore.map.usGeographicCenter")
                    }
                    onGeocoderReady={setGeocoder}
                  />
                </Paper>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    required
                    size="medium"
                    multiline
                    minRows={3}
                    label={t("management.stores.addStore.fields.streetAddress")}
                    value={formData.address}
                    onChange={(e) => {
                      const address = e.target.value;
                      handleInputChange("address", address);
                      if (!canRetainGeocodedLocation || !address.trim()) {
                        setLocationFound(false);
                      }
                    }}
                    placeholder={t("management.stores.addStore.placeholders.streetAddress")}
                  />
                  <TextField
                    fullWidth
                    size="medium"
                    label={t("management.stores.addStore.fields.timezone")}
                    value={formData.timezone}
                    disabled
                    placeholder={t("management.stores.addStore.placeholders.timezone")}
                    helperText={t("management.stores.addStore.helperText.timezone")}
                  />
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: isEditMode ? "repeat(2, minmax(0, 1fr))" : "1fr",
                        },
                        gap: 2,
                      }}
                    >
                      <Button
                        fullWidth
                        type="button"
                        variant="outlined"
                        color="primary"
                        onClick={() => setIsOriginalAddressDialogOpen(true)}
                        startIcon={<HistoryIcon />}
                        disabled={!canViewOriginalAddress}
                      >
                        {t("management.stores.editStore.originalAddress.actions.view")}
                      </Button>
                      {isEditMode && (
                        <Button
                          fullWidth
                          type="button"
                          variant="outlined"
                          color="primary"
                          onClick={() => setIsLegacyFieldsDialogOpen(true)}
                          startIcon={<ViewListIcon />}
                        >
                          {t("management.stores.editStore.legacyAddress.actions.viewFields")}
                        </Button>
                      )}
                    </Box>
                    <Button
                      fullWidth
                      type="button"
                      variant="contained"
                      size="large"
                      onClick={geocodeAddress}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <MyLocationIcon />}
                    >
                      {t("management.stores.addStore.actions.findLocation")}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* Localization */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={sectionHeadingSx}>
                <LanguageIcon />
                {t("management.stores.addStore.sections.localization")}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
                  gap: 3,
                }}
              >
                <Box>
                  <MuiAutocompleteMultiSelect
                    label={t("management.stores.addStore.fields.currencyCode")}
                    options={[...currencyCodes]}
                    value={formData.currencyCode ? [formData.currencyCode] : []}
                    onChange={(next) => {
                      const code = next[0];
                      if (!code) return;
                      handleInputChange("currencyCode", code);
                      handleInputChange("currencySymbol", currencySymbols[code] || "");
                    }}
                    getOptionLabel={(option) => currencyDisplayNames[option]}
                    getOptionKey={(option) => option}
                    required
                    placeholder={t("management.stores.addStore.placeholders.currencyCode")}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    size="medium"
                    label={t("management.stores.addStore.fields.currencySymbol")}
                    value={formData.currencySymbol || ""}
                    disabled
                    InputProps={{ readOnly: true }}
                    placeholder={t("management.stores.addStore.placeholders.currencyCode")}
                    helperText={t("management.stores.addStore.helperText.currencySymbol")}
                    sx={{
                      "& input": {
                        userSelect: "none",
                        cursor: "default",
                      },
                    }}
                  />
                </Box>
                <Box>
                  <MuiAutocompleteMultiSelect
                    label={t("management.stores.addStore.fields.locale")}
                    options={[...locales]}
                    value={formData.locale ? [formData.locale] : []}
                    onChange={(next) => handleInputChange("locale", next[0])}
                    getOptionLabel={(option) => localeDisplayNames[option]}
                    getOptionKey={(option) => option}
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          {/* Store Settings */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={sectionHeadingSx}>
                <SettingsIcon />
                {t("management.stores.addStore.sections.storeSettings")}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                  gap: 3,
                }}
              >
                <Box>
                  <MuiAutocompleteMultiSelect
                    label={t("management.stores.addStore.fields.region")}
                    options={regions}
                    value={regions.filter((r) => r.id === formData.regionId)}
                    onChange={(next) => handleInputChange("regionId", next[0]?.id)}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.id}
                    placeholder={t("management.stores.addStore.placeholders.region")}
                    helperText={t("management.stores.addStore.helperText.region")}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 56 }}>
                  <Stack spacing={0.25}>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={formData.autoCleanEnabled}
                          onChange={(_, checked) => handleInputChange("autoCleanEnabled", checked)}
                        />
                      }
                      label={t("management.stores.addStore.fields.autoVacate")}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t("management.stores.addStore.helperText.autoVacate")}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Paper>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              py: 2,
              px: 1,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !locationFound}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isEditMode ? (
                  <SaveIcon />
                ) : (
                  <FittingRoomIcon />
                )
              }
            >
              {isEditMode
                ? t("management.stores.editStore.actions.saveChanges")
                : t("management.stores.addStore.actions.createAndAddFittingRooms")}
            </Button>
          </Box>
        </Stack>
      </form>

      <AdminDialog
        open={isOriginalAddressDialogOpen}
        onClose={() => setIsOriginalAddressDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        icon={<HistoryIcon />}
        title={t("management.stores.editStore.originalAddress.dialogs.title")}
      >
        <Typography variant="body1" sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
          {originalAddress}
        </Typography>
      </AdminDialog>

      <AdminDialog
        open={isLegacyFieldsDialogOpen}
        onClose={() => setIsLegacyFieldsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        icon={<ViewListIcon />}
        title={t("management.stores.editStore.legacyAddress.dialogs.title")}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {[
            {
              label: "management.stores.editStore.fields.streetAddress",
              value: formData.street,
            },
            { label: "management.stores.editStore.fields.city", value: formData.city },
            { label: "management.stores.editStore.fields.state", value: formData.state },
            { label: "management.stores.editStore.fields.zipCode", value: formData.zip },
            { label: "management.stores.editStore.fields.country", value: formData.country },
          ].map(({ label, value }) => (
            <Stack key={label} spacing={0.25}>
              <Typography variant="body2" sx={locationDetailLabelSx}>
                {t(label)}
              </Typography>
              <Typography variant="body1">
                {value.trim() ? (
                  value
                ) : (
                  <Typography color="action.disabled" component="span">
                    {t("management.devices.devices.empty.notAvailable")}
                  </Typography>
                )}
              </Typography>
            </Stack>
          ))}
        </Box>
      </AdminDialog>
    </PageShell>
  );
};

export default StoreForm;
